import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  LayoutDashboard,
  MapPin,
  CreditCard,
  Users,
  Car,
  Tag,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  Globe,
  ChevronDown,
  Droplets,
  FileText,
  Mail,
  MessageSquare,
  Smartphone,
  Send,
  Bell,
  Receipt,
  Stethoscope,
} from 'lucide-react';

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { t, language } = useLanguage();
  const { isAdmin, isViewer } = useAuth();

  const navGroups = [
    {
      items: [
        { path: '/dashboard', icon: LayoutDashboard, label: 'dashboard', adminOnly: false },
      ],
    },
    {
      title: language === 'ro' ? 'Clienți' : 'Customers',
      items: [
        { path: '/users', icon: Users, label: 'users', adminOnly: true, viewerAllowed: true },
        { path: '/subscriptions', icon: CreditCard, label: 'subscriptions', adminOnly: true, viewerAllowed: true },
        { path: '/subscription-types', icon: Tag, label: 'subscription_types', adminOnly: true },
      ],
    },
    {
      title: language === 'ro' ? 'Operațiuni' : 'Operations',
      items: [
        { path: '/locations', icon: MapPin, label: 'locations', adminOnly: true },
        { path: '/car-catalog', icon: Car, label: 'car_catalog', adminOnly: true },
        { path: '/documents', icon: FileText, label: 'documents', adminOnly: true },
        { path: '/feedback', icon: MessageSquare, label: 'feedback', adminOnly: true },
      ],
    },
    {
      title: language === 'ro' ? 'Comunicare' : 'Communication',
      items: [
        { path: '/sms', icon: Send, label: 'sms', adminOnly: true },
        { path: '/email', icon: Mail, label: 'send_email', adminOnly: true },
        { path: '/app-notifications', icon: Bell, label: 'app_notifications', adminOnly: true },
      ],
    },
    {
      title: language === 'ro' ? 'Setări' : 'Settings',
      items: [
        { path: '/app-settings', icon: Smartphone, label: 'app_settings', adminOnly: true },
        { path: '/email-settings', icon: Mail, label: 'email_settings', adminOnly: true },
        { path: '/facturare', icon: Receipt, label: 'facturare', adminOnly: true },
        { path: '/diagnostics', icon: Stethoscope, label: 'diagnostics', adminOnly: true },
      ],
    },
  ];

  const filterItems = (items) => items.filter(item => {
    if (!item.adminOnly) return !isViewer;
    if (isAdmin) return true;
    if (isViewer && item.viewerAllowed) return true;
    return false;
  });

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-slate-900 text-white transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Droplets className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-base font-['Manrope']">{t('app_name')}</span>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden p-1 hover:bg-slate-800 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100%-4rem)]">
          {navGroups.map((group, gi) => {
            const visible = filterItems(group.items);
            if (visible.length === 0) return null;
            return (
              <div key={gi} className={gi > 0 ? 'pt-3' : ''}>
                {group.title && (
                  <p className="px-4 pb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {group.title}
                  </p>
                )}
                <div className="space-y-1">
                  {visible.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={onClose}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 ${
                        isActive(item.path)
                          ? 'bg-primary text-white'
                          : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                      }`}
                      data-testid={`nav-${item.label}`}
                    >
                      <item.icon className="w-4 h-4 shrink-0" />
                      <span className="font-medium text-sm">{t(item.label)}</span>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
};

const Topbar = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { language, changeLanguage, t } = useLanguage();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const displayName = user ? `${user.first_name} ${user.last_name}` : '';

  return (
    <header className="sticky top-0 z-30 h-16 glass-effect border-b border-border/50">
      <div className="flex items-center justify-between h-full px-4 lg:px-8">
        {/* Menu button (mobile) */}
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 hover:bg-accent rounded-lg transition-colors"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Language Switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2" data-testid="language-switcher">
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline">{language.toUpperCase()}</span>
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => changeLanguage('ro')} data-testid="lang-ro">
                {t('romanian')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => changeLanguage('en')} data-testid="lang-en">
                {t('english')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleTheme}
            data-testid="theme-toggle"
          >
            {theme === 'light' ? (
              <Moon className="w-4 h-4" />
            ) : (
              <Sun className="w-4 h-4" />
            )}
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-sm font-medium text-primary">
                    {user?.first_name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <span className="hidden sm:inline font-medium">{displayName}</span>
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                {t('logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="lg:ml-64">
        <Topbar onMenuClick={() => setSidebarOpen(true)} />
        <main className="p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};
